import React from "react";
import { useTranslation } from "react-i18next";
import { formatUSD } from "@/lib/constants";
import { GOV_FEE_TEXT } from "@/lib/pricing";

// Compact, reusable cost breakdown. Renders inside an existing card shell so it
// keeps the established country/service page styling.
const Row = ({ label, value, muted = false, testid }) => (
  <div className="flex justify-between gap-3" data-testid={testid}>
    <span className={muted ? "text-muted-foreground" : "text-foreground"}>{label}</span>
    <span className="font-mono shrink-0 text-foreground">{value}</span>
  </div>
);

const DEFAULT_RENEWAL_RULE = "Annual renewal billed each year. The renewal date is confirmed at incorporation.";

const GOV_KEYS = {
  included: "country_ui.govFeeIncluded",
  excluded: "country_ui.govFeeExcluded",
  estimated: "country_ui.govFeeEstimated",
};

export default function PricingBreakdown({
  serviceFee = 0,
  serviceLabel,
  govFeeStatus = "included",
  addons = [],
  annualFee = 0,
  renewalRule = "",
  custom = false,
}) {
  const { t } = useTranslation();
  const addonTotal = (addons || []).reduce((s, a) => s + Number(a.price || 0), 0);
  const total = Number(serviceFee || 0) + addonTotal;
  const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");

  if (custom) {
    return (
      <div className="mt-4 space-y-2 text-sm border-t border-border pt-4" data-testid="pricing-breakdown">
        <p className="font-heading text-lg text-primary">{t("country_ui.customQuotation")}</p>
        <p className="text-xs text-muted-foreground">{t("country_ui.customNote")}</p>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-2 text-sm border-t border-border pt-4" data-testid="pricing-breakdown">
      <Row label={t("country_ui.pbService", { defaultValue: serviceLabel || "Service & registry (all-inclusive)" })} value={formatUSD(serviceFee)} muted testid="pb-service-fee" />
      {govFeeStatus && GOV_FEE_TEXT[govFeeStatus] && GOV_KEYS[govFeeStatus] && (
        <p className="text-[11px] text-muted-foreground" data-testid="pb-gov-fee">{t(GOV_KEYS[govFeeStatus], { defaultValue: GOV_FEE_TEXT[govFeeStatus] })}</p>
      )}
      {(addons || []).map((a) => (
        <Row key={a.name} label={t(`wizard.addon.${slugify(a.name)}`, { defaultValue: a.name })} value={formatUSD(a.price)} muted testid={`pb-addon-${a.name}`} />
      ))}
      <div className="flex justify-between gap-3 border-t border-border pt-2 mt-1 font-medium" data-testid="pb-total">
        <span className="text-foreground">{t("country_ui.pbTotal")}</span>
        <span className="font-mono shrink-0 text-primary">{formatUSD(total)}</span>
      </div>
      {annualFee > 0 && (
        <Row label={t("country_ui.pbAnnualRenewal")} value={`${formatUSD(annualFee)}${t("country_ui.perYear", { defaultValue: "/yr" })}`} muted testid="pb-renewal" />
      )}
      {renewalRule && <p className="text-[11px] text-muted-foreground" data-testid="pb-renewal-rule">{renewalRule === DEFAULT_RENEWAL_RULE ? t("country_ui.renewalRuleDefault", { defaultValue: DEFAULT_RENEWAL_RULE }) : renewalRule}</p>}
    </div>
  );
}
